<template>
	<AppSelect
		outlined
		:value="currentSubflowId"
		:items="existingSubflows"
		:menu-props="{ contentClass: 'apps-select-empty-state-menu', 'nudge-top': '4px', closeOnContentClick: true }"
		@change="onChange"
	>
		<template v-slot:no-data>
			<div class="no-subflows display-flex flex-column">
				<div class="flex-centerize flex-column flex-1">
					<div class="font-weight-700 mb-3">No subflows here yet</div>
					<div class="font-weight-400 subtle-message">Subflows allow you to reuse a</div>
					<div class="font-weight-400 subtle-message">collection of steps in multiple features.</div>
					<div class="font-weight-500 mt-3">Create your first subflow below</div>
				</div>
			</div>
		</template>
		<template v-slot:item="{ item }">
			<div v-if="item.itemType === 'subflow_item'" class="display-flex flex-column">
				{{ item.text }}
			</div>
			<div v-else-if="hasUnpublishedSubflows" class="unpublished-spacer font-size-xs soft-message">Unpublished subflow</div>
			<div v-else class="dummy-select-item"><!-- prevent exception of empty item in v-select  --></div>
		</template>
		<template v-slot:append-item>
			<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId">
				<template #default="{ indicator, isUpgradeRequired }">
					<div class="create-subflow-button flex-centerize" @click.stop="addSubflow">
						+ Create subflow
						<component :is="indicator" v-if="isUpgradeRequired" :billingFeatureId="billingFeatureId" iconOnly canBuild class="ml-2" />
					</div>
				</template>
			</MaybeUpgradeRequiredIndicator>
		</template>
	</AppSelect>
</template>

<script>
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { createSubflow } from '@/store/modules/selectedWorkflow/actions';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { fetchSubflows } from '@/store/modules/subflows/actions';
import { projectSubflows } from '@/store/modules/subflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
export default {
	name: 'SubflowSelect',
	components: { MaybeUpgradeRequiredIndicator },
	props: {
		projectId: { type: Number },
		currentSubflowId: { type: Number },
	},
	data() {
		return { subflowName: '', loading: false };
	},
	computed: {
		...mapGettersShorthand({ projectSubflows, editWorkflow }),
		subflows() {
			return this.projectSubflows(this.projectId).filter((subflow) => subflow.id !== this.editWorkflow.id);
		},
		existingSubflows() {
			let items = this.subflows.map((flow) => ({
				text: flow.workflow_title,
				value: flow.id,
				itemType: 'subflow_item',
				isPublished: flow.is_published,
				disabled: false,
			}));
			const publishedItems = items.filter((a) => a.isPublished);
			const unPublishedItems = items.filter((a) => !a.isPublished);
			items = publishedItems.concat([{ itemType: 'spacer_item', disabled: true }, ...unPublishedItems]);
			return items.length === 1 ? [] : items;
		},
		hasUnpublishedSubflows() {
			return this.subflows.some((flow) => !flow.is_published);
		},
		billingFeatureId() {
			return FeatureTypes.SubFlows;
		},
	},
	methods: {
		...mapActionsShorthand({ createSubflow, fetchSubflows }),
		async addSubflow() {
			const ret = await AppDialogManagerService.openNewSubflowBlockDialog({ projectId: this.editWorkflow.project });
			if (ret) {
				this.$emit('onSubflowSelected', ret.subflowId);
			}
		},
		onChange(id) {
			if (id && !isNaN(id)) {
				this.$emit('onSubflowSelected', id);
			}
		},
	},
	mounted() {
		this.fetchSubflows(this.projectId);
	},
};
</script>

<style lang="scss">
.apps-select-empty-state-menu {
	box-shadow: none !important;
	border-radius: 8px !important;
	border: 1px solid #e8ebf3 !important;
	box-shadow: var(--card-shadow) !important;

	.v-select-list {
		padding: 0 !important;
	}
	.v-list-item:has(.dummy-select-item) {
		min-height: 1px;
		max-height: 1px;
	}
}
</style>

<style scoped lang="scss">
.no-subflows {
	min-height: 260px;
	font-size: 12px;
}

.create-subflow-button {
	position: sticky;
	bottom: 0;
	min-height: 40px;
	background-color: var(--ask-for-ingreation-background);
	color: #6291ed;
	border-top: 1px solid #e8ebf3 !important;
	border-bottom-left-radius: 3px;
	border-bottom-right-radius: 3px;

	&:hover {
		filter: brightness(99%);
		cursor: pointer;
		transition: 0.2s;
	}
	&:not(:hover) {
		transition: 0.3s;
	}
}

.unpublished-spacer {
	width: 100%;
	min-height: 10px;
	padding-top: 5px;
	border-top: 1px solid var(--border-color) !important;
	pointer-events: none !important;
}
.dummy-select-item {
	max-height: 1px !important;
	min-height: 1px !important;
}
</style>
